import React from 'react';
import { motion, LayoutGroup } from 'framer-motion';
import styles from '../../styles/components/ViewSwitcher.module.scss';

// ============================================================================
// Типы
// ============================================================================

export interface ViewOption<T extends string = string> {
  value: T;
  label: string;
  icon?: React.ReactNode;
}

interface ViewSwitcherProps<T extends string = string> {
  options: ViewOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Уникальный ID для анимации (нужен если на странице несколько ViewSwitcher) */
  layoutId?: string;
  /**
   * 'glass' — standalone, стеклянная капсула (по умолчанию)
   * 'embedded' — для встраивания внутри тулбаров/карточек (приглушённый фон, без blur)
   */
  variant?: 'glass' | 'embedded';
}

// ============================================================================
// Анимация
// ============================================================================

const pillTransition = {
  type: 'spring' as const,
  stiffness: 500,
  damping: 35,
};

// ============================================================================
// Компонент
// ============================================================================

/**
 * Универсальный переключатель представлений.
 * Glass-pill стиль со spring-анимацией скользящей подложки.
 *
 * Варианты:
 * - `glass` — для самостоятельного размещения на странице
 * - `embedded` — для встраивания в тулбары и другие контейнеры
 */
const ViewSwitcherInner = <T extends string>({
  options,
  value,
  onChange,
  layoutId = 'view-switcher',
  variant = 'glass',
}: ViewSwitcherProps<T>) => (
  <LayoutGroup id={layoutId}>
    <div
      className={`${styles.container} ${variant === 'embedded' ? styles.embedded : ''}`}
      role="radiogroup"
    >
      {options.map((option) => {
        const isActive = option.value === value;

        return (
          <button
            key={option.value}
            role="radio"
            aria-checked={isActive}
            className={`${styles.option} ${isActive ? styles.active : ''}`}
            onClick={() => onChange(option.value)}
            type="button"
          >
            {isActive && (
              <motion.span
                className={styles.activePill}
                layoutId={`${layoutId}-pill`}
                transition={pillTransition}
              />
            )}
            {option.icon && <span className={styles.icon}>{option.icon}</span>}
            <span className={styles.label}>{option.label}</span>
          </button>
        );
      })}
    </div>
  </LayoutGroup>
);

export const ViewSwitcher = React.memo(ViewSwitcherInner) as typeof ViewSwitcherInner;
